import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Alert, DeviceEventEmitter } from 'react-native';
import { Image } from 'expo-image';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Svg, Path, Defs, RadialGradient, Stop, G, Ellipse } from 'react-native-svg';
import * as WebBrowser from 'expo-web-browser';
import PageHeader from '@/components/PageHeader';
import { profileService } from '@/services/profileService';
import { useServerVerifiedPlus } from '@/hooks/useEntitlement';
import { useOfferings } from '@/hooks/useOfferings';
import RevenueCatService from '@/services/revenueCatService';
import { PAYWALL_RESULT } from 'react-native-purchases-ui';
import { fetchUserClinic, Clinic } from '../services/clinicsApi';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import MegaphoneIcon from '@/components/icons/MegaphoneIcon';
import TrailFlagIcon from '@/components/icons/TrailFlagIcon';

export default function SubscriptionScreen() {
  const router = useRouter();
  const { t, currentLanguage } = useLanguage();
  const { session, sessionReady } = useAuth();
  const [invitationDate, setInvitationDate] = useState<string>('');
  const [showingPaywall, setShowingPaywall] = useState(false);
  const [clinicData, setClinicData] = useState<Clinic | null>(null);
  const [logoHeight, setLogoHeight] = useState<number | null>(null);
  const LOGO_WIDTH = 140;
  const { hasPadrinhoPlus, forceRefresh } = useServerVerifiedPlus();
  const { packages } = useOfferings();
  
  const monthlyPackage = packages.find(p =>
    p.packageType === 'MONTHLY' || p.identifier === '$rc_monthly'
  ) ?? packages[0] ?? null;
  const subscriptionPrice = monthlyPackage
    ? monthlyPackage.product.priceString + (currentLanguage === 'pt' ? '/mês' : currentLanguage === 'es' ? '/mes' : '/mo')
    : currentLanguage === 'pt' ? 'R$ 29,90/mês' : currentLanguage === 'es' ? '$4.99/mes' : '$4.99/mo';

  const CardBackground = () => (
    <View style={styles.cardBackgroundContainer}>
      <Svg width="100%" height="100%" viewBox="0 0 469 207" preserveAspectRatio="xMidYMid slice">
        <Defs>
          <RadialGradient id="glow1" cx="50%" cy="50%" rx="50%" ry="50%">
            <Stop offset="0%" stopColor="white" stopOpacity="0.9" />
            <Stop offset="40%" stopColor="white" stopOpacity="0.4" />
            <Stop offset="100%" stopColor="white" stopOpacity="0" />
          </RadialGradient>
          <RadialGradient id="glow2" cx="50%" cy="50%" rx="50%" ry="50%">
            <Stop offset="0%" stopColor="#015480" stopOpacity="1" />
            <Stop offset="50%" stopColor="#015480" stopOpacity="0.5" />
            <Stop offset="100%" stopColor="#015480" stopOpacity="0" />
          </RadialGradient>
          <RadialGradient id="glow3" cx="50%" cy="50%" rx="50%" ry="50%">
            <Stop offset="0%" stopColor="#015480" stopOpacity="0.9" />
            <Stop offset="50%" stopColor="#015480" stopOpacity="0.4" />
            <Stop offset="100%" stopColor="#015480" stopOpacity="0" />
          </RadialGradient>
        </Defs>
        <G opacity={0.9}>
          <Ellipse cx="60" cy="120" rx="140" ry="130" fill="url(#glow1)" />
        </G>
        <Ellipse cx="300" cy="140" rx="150" ry="140" fill="url(#glow2)" />
        <Ellipse cx="420" cy="40" rx="100" ry="100" fill="url(#glow3)" />
      </Svg>
    </View>
  );

  useEffect(() => {
    if (sessionReady) {
      loadSubscriptionData();
    }
  }, [sessionReady]);

  // Listen for subscription status changes (from RevenueCat sync)
  useEffect(() => {
    const subscription = DeviceEventEmitter.addListener(
      'subscription:status-changed',
      async (data: { hasPadrinhoPlus: boolean }) => {
        console.log('💎 Subscription: Status changed:', data.hasPadrinhoPlus);
        // Refresh entitlement state to update UI
        await forceRefresh();
      }
    );

    return () => subscription.remove();
  }, [forceRefresh]);

  const loadSubscriptionData = async () => {
    try {
      let profile = null;
      try {
        profile = await profileService.getProfile();
      } catch (profileError) {
        console.warn('⚠️ Could not fetch profile for subscription date:', profileError);
      }
      
      // Map language to locale
      const localeMap: Record<string, string> = {
        pt: 'pt-BR',
        en: 'en-US',
        es: 'es-ES'
      };
      const locale = localeMap[currentLanguage] || 'en-US';

      if (profile?.created_at) {
        const date = new Date(profile.created_at);
        const formattedDate = date.toLocaleDateString(locale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        setInvitationDate(`${t('account.subscription.since')} ${formattedDate}`);
      }

      // Load clinic data
      const clinic = await fetchUserClinic(session?.access_token);
      if (clinic) {
        setClinicData(clinic);
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
    }
  };

  const handleUpgrade = async () => {
    if (showingPaywall || hasPadrinhoPlus) return;

    try {
      setShowingPaywall(true);
      const { result } = await RevenueCatService.presentPaywall({ fromSurface: 'subscription_screen' });
      
      if (result === PAYWALL_RESULT.PURCHASED || result === PAYWALL_RESULT.RESTORED) {
        await forceRefresh();
        Alert.alert(t('account.subscription.successUpgrade'), t('account.subscription.successUpgradeMessage'));
      } else if (result === PAYWALL_RESULT.ERROR) {
        Alert.alert(
          t('account.subscription.error'),
          t('account.subscription.errorMessage')
        );
      }
    } catch (error) {
      console.error('Error showing paywall:', error);
      Alert.alert(
        t('account.subscription.error'), 
        t('account.subscription.errorMessage')
      );
    } finally {
      setShowingPaywall(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const previousStatus = hasPadrinhoPlus;
      console.log('💎 Opening customer center, current status:', previousStatus ? 'Plus' : 'Base');
      
      await RevenueCatService.presentCustomerCenter();
      
      // Delayed refresh with retry logic to catch subscription changes
      // RevenueCat servers may take a few seconds to process cancellations
      console.log('🔄 Starting delayed refresh to catch subscription changes...');
      
      // Immediate refresh
      await forceRefresh();
      
      // Additional delayed refresh after 2 seconds
      setTimeout(async () => {
        console.log('🔄 Delayed refresh (2s)...');
        await forceRefresh();
      }, 2000);
      
      // Final refresh after 4 seconds to ensure status is updated
      setTimeout(async () => {
        console.log('🔄 Final refresh (4s)...');
        await forceRefresh();
      }, 4000);
      
    } catch (error) {
      console.error('Error showing subscription management:', error);
    }
  };

  const handleTermsOfUse = async () => {
    try {
      const url = 'https://www.padrinho.app/termos';
      await WebBrowser.openBrowserAsync(url, {
        presentationStyle: WebBrowser.WebBrowserPresentationStyle.PAGE_SHEET,
        controlsColor: '#015480',
      });
    } catch (error) {
      console.error('Error opening Terms of Use:', error);
      Alert.alert(t('common.alert.errorTitle'), t('account.subscription.errorTermsOfUse'));
    }
  };

  const handleProvider = () => {
    router.push('/provider');
  };

  const renderLogo = () => {
    if (clinicData?.logo_path) {
      return (
        <Image
          source={{ uri: clinicData.logo_path }}
          style={[
            styles.clinicLogo,
            logoHeight !== null ? { height: logoHeight } : { height: 48 },
          ]}
          contentFit="contain"
          onLoad={(e) => {
            const { width, height } = e.source;
            if (width && height) {
              const ratio = height / width;
              setLogoHeight(Math.round(LOGO_WIDTH * ratio));
            }
          }}
          onError={() => {
            setLogoHeight(48);
          }}
        />
      );
    }

    return (
      <Text style={styles.providerNameText}>
        {clinicData?.name || t('account.provider.title')}
      </Text>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <PageHeader title={t('account.subscription.pageTitle')} onBack={() => router.back()} backgroundColor="#FFFFFF" />

      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Plan Header Section */}
        <View style={styles.planSection}>
          <View style={styles.planHeader}>
            {/* Plan Icon - Diamond for Plus, Compass for Base */}
            {hasPadrinhoPlus ? (
              <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <Path d="M5.85078 19.2123L18.0272 32.5903C19.0845 33.753 20.9137 33.753 21.9708 32.5903L34.149 19.2107C35.1673 18.0935 35.284 16.4233 34.4327 15.173L29.529 7.96995C28.921 7.07809 27.9092 6.54297 26.8292 6.54297H13.1852C12.1052 6.54297 11.095 7.07647 10.4869 7.96834L5.56862 15.173C4.71567 16.4216 4.83243 18.0935 5.85078 19.2123Z" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M23.3335 6.54297L25.6118 15.173C25.9424 16.4233 25.8971 18.0935 25.5014 19.2107L20.7681 32.5903C20.3578 33.753 19.6459 33.753 19.2356 32.5903L14.5023 19.2123C14.1066 18.0935 14.0612 16.4216 14.3936 15.173L16.6785 6.54297" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M5.01172 17.0054H34.9834" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
            ) : (
              <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <Path d="M34.3986 19.5968C34.3986 11.6457 27.9527 5.19824 20 5.19824C12.0474 5.19824 5.60156 11.6457 5.60156 19.5968C5.60156 27.5494 12.0474 33.9952 20 33.9952C27.9527 33.9952 34.3986 27.5494 34.3986 19.5968Z" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M20.003 5.19824V7.92696M34.4016 19.5968H31.6715M20.003 33.9952V31.2681M8.33027 19.5968H5.60156" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path fillRule="evenodd" clipRule="evenodd" d="M14.9922 24.6027L17.3784 16.9785L25.0026 14.5923L22.6164 22.2164L14.9922 24.6027Z" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
            )}

            {/* Plan Name */}
            <Text style={styles.planName}>{hasPadrinhoPlus ? 'Padrinho Plus' : 'Padrinho Base'}</Text>

            {/* Badge and Date */}
            <View style={styles.badgeRow}>
              <View style={styles.badge}>
                <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <Path d="M12.6654 4.7915L6.2487 11.2082L3.33203 8.2915" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
                <Text style={styles.badgeText}>{t('account.subscription.activeBadge')}</Text>
              </View>
              <Text style={styles.dateText}>{invitationDate}</Text>
            </View>
          </View>

          {/* Plan Description */}
          <Text style={styles.planDescription}>
            {hasPadrinhoPlus 
              ? t('account.subscription.planDescriptionPlus')
              : t('account.subscription.planDescriptionBase')}
          </Text>
        </View>

        {/* Subscription Action Cards */}
        <View style={styles.upgradeSection}>
          {!hasPadrinhoPlus ? (
            <>
              {/* Upgrade Card - Only show if not Plus */}
              <TouchableOpacity 
                style={styles.upgradeCard} 
                onPress={handleUpgrade}
                disabled={showingPaywall}
              >
                <CardBackground />
                <View style={styles.upgradeIcon}>
                  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <Path d="M2.92539 9.60617L9.01358 16.2952C9.54225 16.8765 10.4568 16.8765 10.9854 16.2952L17.0745 9.60533C17.5837 9.04675 17.642 8.21163 17.2163 7.58651L14.7645 3.98498C14.4605 3.53904 13.9546 3.27148 13.4146 3.27148H6.59259C6.0526 3.27148 5.54748 3.53823 5.24343 3.98417L2.78431 7.58651C2.35783 8.21082 2.41622 9.04675 2.92539 9.60617Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M11.6667 3.27148L12.8059 7.58651C12.9712 8.21163 12.9486 9.04675 12.7507 9.60533L10.3841 16.2952C10.1789 16.8765 9.82297 16.8765 9.61781 16.2952L7.25114 9.60617C7.05331 9.04675 7.03061 8.21082 7.19682 7.58651L8.33923 3.27148" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M2.50781 8.50244H17.4936" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </Svg>
                </View>
                <View style={styles.upgradeContent}>
                  <Text style={styles.upgradeTitle}>{t('account.subscription.upgradeTitle')}</Text>
                  <Text style={styles.upgradeSubtitle}>{t('account.subscription.upgradeSubtitle')}</Text>
                </View>
                <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <Path d="M5.83203 3.3335L10.4987 8.00016L5.83203 12.6668" stroke="white" strokeWidth="2" strokeLinecap="square"/>
                </Svg>
              </TouchableOpacity>

              {/* Provider Section */}
              <TouchableOpacity style={styles.providerCard} onPress={handleProvider}>
                <View style={styles.providerHeader}>
                  {renderLogo()}
                  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <Path d="M5.83203 3.3335L10.4987 8.00016L5.83203 12.6668" stroke="#151515" strokeWidth="2" strokeLinecap="square"/>
                  </Svg>
                </View>
                <Text style={styles.providerText}>{t('account.subscription.providerText')}</Text>
              </TouchableOpacity>
            </>
          ) : (
            /* Manage Subscription Card - Blue design for Plus subscribers */
            <TouchableOpacity 
              style={styles.upgradeCard} 
              onPress={handleManageSubscription}
            >
              <CardBackground />
              <View style={styles.upgradeIcon}>
                <Svg width="24" height="24" viewBox="0 0 40 40" fill="none">
                  <Path d="M5.85078 19.2123L18.0272 32.5903C19.0845 33.753 20.9137 33.753 21.9708 32.5903L34.149 19.2107C35.1673 18.0935 35.284 16.4233 34.4327 15.173L29.529 7.96995C28.921 7.07809 27.9092 6.54297 26.8292 6.54297H13.1852C12.1052 6.54297 11.095 7.07647 10.4869 7.96834L5.56862 15.173C4.71567 16.4216 4.83243 18.0935 5.85078 19.2123Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M23.3335 6.54297L25.6118 15.173C25.9424 16.4233 25.8971 18.0935 25.5014 19.2107L20.7681 32.5903C20.3578 33.753 19.6459 33.753 19.2356 32.5903L14.5023 19.2123C14.1066 18.0935 14.0612 16.4216 14.3936 15.173L16.6785 6.54297" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M5.01172 17.0054H34.9834" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.upgradeContent}>
                <Text style={styles.upgradeTitle}>{t('account.subscription.mySubscription')}</Text>
                <Text style={styles.upgradeSubtitle}>{subscriptionPrice}</Text>
              </View>
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path d="M5.83203 3.3335L10.4987 8.00016L5.83203 12.6668" stroke="white" strokeWidth="2" strokeLinecap="square"/>
              </Svg>
            </TouchableOpacity>
          )}
        </View>

        {/* Features Section */}
        <View style={styles.featuresSection}>
          {/* Bill Feature */}
          <View style={styles.featureCard}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path d="M11.0938 3.94182C11.4529 3.17234 12.5471 3.17233 12.9062 3.94182L14.2592 6.84105C14.8552 8.11823 15.8818 9.14483 17.1589 9.74084L20.0582 11.0938C20.8277 11.4529 20.8277 12.5471 20.0582 12.9062L17.1589 14.2592C15.8818 14.8552 14.8552 15.8818 14.2592 17.1589L12.9062 20.0582C12.5471 20.8277 11.4529 20.8277 11.0938 20.0582L9.74084 17.1589C9.14483 15.8818 8.11823 14.8552 6.84106 14.2592L3.94182 12.9062C3.17234 12.5471 3.17233 11.4529 3.94182 11.0938L6.84105 9.74084C8.11823 9.14483 9.14483 8.11823 9.74084 6.84105L11.0938 3.94182Z" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <View style={styles.featureContent}>
              <Text style={styles.featureTitle}>{t('account.subscription.features.bill.title')}</Text>
              <Text style={styles.featureDescription}>{t('account.subscription.features.bill.description')}</Text>
            </View>
          </View>

          {/* Bill Plus Feature - NEW */}
          <View style={styles.featureCard}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path d="M11.0938 3.94182C11.4529 3.17234 12.5471 3.17233 12.9062 3.94182L14.2592 6.84105C14.8552 8.11823 15.8818 9.14483 17.1589 9.74084L20.0582 11.0938C20.8277 11.4529 20.8277 12.5471 20.0582 12.9062L17.1589 14.2592C15.8818 14.8552 14.8552 15.8818 14.2592 17.1589L12.9062 20.0582C12.5471 20.8277 11.4529 20.8277 11.0938 20.0582L9.74084 17.1589C9.14483 15.8818 8.11823 14.8552 6.84106 14.2592L3.94182 12.9062C3.17234 12.5471 3.17233 11.4529 3.94182 11.0938L6.84105 9.74084C8.11823 9.14483 9.14483 8.11823 9.74084 6.84105L11.0938 3.94182Z" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <View style={styles.featureContent}>
              <View style={styles.featureTitleRow}>
                <Text style={styles.featureTitle}>{t('account.subscription.features.billPlus.title')}</Text>
                <View style={styles.plusBadge}>
                  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <Path d="M2.34031 7.68493L7.21087 13.0361C7.6338 13.5012 8.36547 13.5012 8.78833 13.0361L13.6596 7.68427C14.0669 7.2374 14.1136 6.5693 13.7731 6.06921L11.8116 3.18798C11.5684 2.83123 11.1637 2.61719 10.7317 2.61719H5.27407C4.84208 2.61719 4.43799 2.83059 4.19475 3.18733L2.22745 6.06921C1.88627 6.56865 1.93297 7.2374 2.34031 7.68493Z" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M9.3326 2.61719L10.2439 6.06921C10.3762 6.5693 10.3581 7.2374 10.1998 7.68427L8.30647 13.0361C8.14233 13.5012 7.8576 13.5012 7.69347 13.0361L5.80013 7.68493C5.64187 7.2374 5.62371 6.56865 5.75667 6.06921L6.6706 2.61719" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M2.00391 6.80225H13.9926" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                  </Svg>
                  <Text style={styles.plusBadgeText}>Plus</Text>
                </View>
              </View>
              <Text style={styles.featureDescription}>{t('account.subscription.features.billPlus.description')}</Text>
            </View>
          </View>

          {/* Scheduling & Professionals Feature - Keep Plus badge */}
          <View style={styles.featureCard}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path d="M15.1523 3.69238V6.6677" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M7.92969 3.69238V6.6677" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <Path fillRule="evenodd" clipRule="evenodd" d="M15.683 5.06445L7.40863 5.06527C4.97812 5.06527 3.46094 6.41874 3.46094 8.90651V16.3957C3.46094 18.9229 4.97812 20.3077 7.40863 20.3077L15.6759 20.3068C18.1136 20.3068 19.6235 18.9462 19.6235 16.4576V8.90569C19.6307 6.41792 18.1207 5.06445 15.683 5.06445Z" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M7.52344 11.4375H7.53344" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M7.52344 14.8916H7.53344" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M11.5391 11.4375H11.5491" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M11.5391 14.8916H11.5491" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M15.5391 11.4375H15.5491" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M15.5391 14.8916H15.5491" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </Svg>
            <View style={styles.featureContent}>
              <View style={styles.featureTitleRow}>
                <Text style={styles.featureTitle}>{t('account.subscription.features.scheduling.title')}</Text>
                <View style={styles.plusBadge}>
                  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <Path d="M2.34031 7.68493L7.21087 13.0361C7.6338 13.5012 8.36547 13.5012 8.78833 13.0361L13.6596 7.68427C14.0669 7.2374 14.1136 6.5693 13.7731 6.06921L11.8116 3.18798C11.5684 2.83123 11.1637 2.61719 10.7317 2.61719H5.27407C4.84208 2.61719 4.43799 2.83059 4.19475 3.18733L2.22745 6.06921C1.88627 6.56865 1.93297 7.2374 2.34031 7.68493Z" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M9.3326 2.61719L10.2439 6.06921C10.3762 6.5693 10.3581 7.2374 10.1998 7.68427L8.30647 13.0361C8.14233 13.5012 7.8576 13.5012 7.69347 13.0361L5.80013 7.68493C5.64187 7.2374 5.62371 6.56865 5.75667 6.06921L6.6706 2.61719" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M2.00391 6.80225H13.9926" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                  </Svg>
                  <Text style={styles.plusBadgeText}>Plus</Text>
                </View>
              </View>
              <Text style={styles.featureDescription}>{t('account.subscription.features.scheduling.description')}</Text>
            </View>
          </View>

          {/* Meetings Feature - No badge */}
          <View style={styles.featureCard}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path d="M18.4222 14.1112C18.4222 14.1112 9.21383 15.4666 8.05792 15.5872C6.90202 15.7079 5.07183 14.8011 4.42091 13.0117C3.76998 11.2224 4.58826 9.35138 5.55151 8.70142C6.51477 8.0505 14.4018 3.19336 14.4018 3.19336" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M8.29688 15.5546L9.77289 19.61C10.1718 20.7056 11.3822 21.2709 12.4778 20.871C13.5734 20.4731 14.1377 19.2617 13.7397 18.1671L12.5897 15.0078" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path fillRule="evenodd" clipRule="evenodd" d="M19.1084 7.66539C20.2118 10.6962 20.0026 13.5558 18.6404 14.0511C17.2782 14.5473 15.2797 12.4914 14.1763 9.46055C13.073 6.42873 13.2832 3.5701 14.6444 3.07388C16.0065 2.57766 18.005 4.63357 19.1084 7.66539Z" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <View style={styles.featureContent}>
              <Text style={styles.featureTitle}>{t('account.subscription.features.meetings.title')}</Text>
              <Text style={styles.featureDescription}>{t('account.subscription.features.meetings.description')}</Text>
            </View>
          </View>

          {/* Encontros Exclusivos Feature - Plus badge */}
          <View style={styles.featureCard}>
            <MegaphoneIcon size={24} color="#669AB7" />
            <View style={styles.featureContent}>
              <View style={styles.featureTitleRow}>
                <Text style={styles.featureTitle}>{t('account.subscription.features.exclusiveMeetings.title')}</Text>
                <View style={styles.plusBadge}>
                  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <Path d="M2.34031 7.68493L7.21087 13.0361C7.6338 13.5012 8.36547 13.5012 8.78833 13.0361L13.6596 7.68427C14.0669 7.2374 14.1136 6.5693 13.7731 6.06921L11.8116 3.18798C11.5684 2.83123 11.1637 2.61719 10.7317 2.61719H5.27407C4.84208 2.61719 4.43799 2.83059 4.19475 3.18733L2.22745 6.06921C1.88627 6.56865 1.93297 7.2374 2.34031 7.68493Z" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M9.3326 2.61719L10.2439 6.06921C10.3762 6.5693 10.3581 7.2374 10.1998 7.68427L8.30647 13.0361C8.14233 13.5012 7.8576 13.5012 7.69347 13.0361L5.80013 7.68493C5.64187 7.2374 5.62371 6.56865 5.75667 6.06921L6.6706 2.61719" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M2.00391 6.80225H13.9926" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                  </Svg>
                  <Text style={styles.plusBadgeText}>Plus</Text>
                </View>
              </View>
              <Text style={styles.featureDescription}>{t('account.subscription.features.exclusiveMeetings.description')}</Text>
            </View>
          </View>

          {/* Trilhas Exclusivas Feature - Plus badge */}
          <View style={styles.featureCard}>
            <TrailFlagIcon size={24} color="#669AB7" />
            <View style={styles.featureContent}>
              <View style={styles.featureTitleRow}>
                <Text style={styles.featureTitle}>{t('account.subscription.features.exclusiveTrails.title')}</Text>
                <View style={styles.plusBadge}>
                  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <Path d="M2.34031 7.68493L7.21087 13.0361C7.6338 13.5012 8.36547 13.5012 8.78833 13.0361L13.6596 7.68427C14.0669 7.2374 14.1136 6.5693 13.7731 6.06921L11.8116 3.18798C11.5684 2.83123 11.1637 2.61719 10.7317 2.61719H5.27407C4.84208 2.61719 4.43799 2.83059 4.19475 3.18733L2.22745 6.06921C1.88627 6.56865 1.93297 7.2374 2.34031 7.68493Z" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M9.3326 2.61719L10.2439 6.06921C10.3762 6.5693 10.3581 7.2374 10.1998 7.68427L8.30647 13.0361C8.14233 13.5012 7.8576 13.5012 7.69347 13.0361L5.80013 7.68493C5.64187 7.2374 5.62371 6.56865 5.75667 6.06921L6.6706 2.61719" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                    <Path d="M2.00391 6.80225H13.9926" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                  </Svg>
                  <Text style={styles.plusBadgeText}>Plus</Text>
                </View>
              </View>
              <Text style={styles.featureDescription}>{t('account.subscription.features.exclusiveTrails.description')}</Text>
            </View>
          </View>

          {/* Progress Feature */}
          <View style={styles.featureCard}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path d="M12 2V6" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M12 18V22" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M22 12H18" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M6 12H2" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M4.92969 4.92871L7.75811 7.75714" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M16.2422 16.2427L19.0706 19.0711" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M19.0703 4.92871L16.2419 7.75714" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M7.75781 16.2427L4.92939 19.0711" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <View style={styles.featureContent}>
              <Text style={styles.featureTitle}>{t('account.subscription.features.progress.title')}</Text>
              <Text style={styles.featureDescription}>{t('account.subscription.features.progress.description')}</Text>
            </View>
          </View>

          {/* Sobriety Clock Feature - No badge */}
          <View style={styles.featureCard}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path fillRule="evenodd" clipRule="evenodd" d="M16.334 3H7.665C4.644 3 2.75 5.139 2.75 8.166V16.334C2.75 19.361 4.634 21.5 7.665 21.5H16.333C19.364 21.5 21.25 19.361 21.25 16.334V8.166C21.25 5.139 19.364 3 16.334 3Z" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M15.391 14.2673L12 12.2443V7.8833" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <View style={styles.featureContent}>
              <Text style={styles.featureTitle}>{t('account.subscription.features.clock.title')}</Text>
              <Text style={styles.featureDescription}>{t('account.subscription.features.clock.description')}</Text>
            </View>
          </View>

          {/* Diary Feature - No badge */}
          <View style={styles.featureCard}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path d="M19 7.1875L9.375 16.8125L5 12.4375" stroke="#669AB7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <View style={styles.featureContent}>
              <Text style={styles.featureTitle}>{t('account.subscription.features.diary.title')}</Text>
              <Text style={styles.featureDescription}>{t('account.subscription.features.diary.description')}</Text>
            </View>
          </View>
        </View>

        {/* Divider */}
        <View style={styles.divider} />

        {/* Security Footer */}
        <View style={styles.securitySection}>
          <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <Path fillRule="evenodd" clipRule="evenodd" d="M11.4845 21.606C11.4845 21.606 19.1565 19.283 19.1565 12.879C19.1565 6.474 19.4345 5.974 18.8195 5.358C18.2035 4.742 12.4905 2.75 11.4845 2.75C10.4785 2.75 4.76549 4.742 4.15049 5.358C3.53449 5.974 3.81249 6.474 3.81249 12.879C3.81249 19.283 11.4845 21.606 11.4845 21.606Z" stroke="#757C85" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <Path d="M8.88672 11.8746L10.7787 13.7696L14.6767 9.86963" stroke="#757C85" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </Svg>
          <Text style={styles.securityText}>{t('account.subscription.securityText')}</Text>
        </View>

        {/* Divider */}
        <View style={styles.termsDivider} />

        {/* Terms of Use Link - Required by Apple for auto-renewable subscriptions */}
        <TouchableOpacity 
          style={styles.termsLink} 
          onPress={handleTermsOfUse}
        >
          <Text style={styles.termsLinkText}>{t('account.subscription.termsLink')}</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollView: {
    flex: 1,
  },
  planSection: {
    paddingHorizontal: 40,
    paddingVertical: 40,
    alignItems: 'center',
    gap: 16,
  },
  planHeader: {
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  planName: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28.8,
    textAlign: 'center',
  },
  badgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingLeft: 4,
    paddingRight: 8,
    backgroundColor: '#669AB7',
    borderRadius: 14,
    height: 24,
  },
  badgeText: {
    fontSize: 10,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#FFFFFF',
    lineHeight: 14,
  },
  dateText: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '400',
    color: '#151515',
    lineHeight: 19.6,
  },
  planDescription: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '400',
    color: '#757C85',
    lineHeight: 19.6,
    textAlign: 'center',
  },
  upgradeSection: {
    paddingHorizontal: 24,
    gap: 12,
    marginBottom: 24,
  },
  upgradeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingVertical: 16,
    paddingLeft: 16,
    paddingRight: 24,
    backgroundColor: '#669AB7',
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  cardBackgroundContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  upgradeIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(1, 84, 128, 0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  upgradeContent: {
    flex: 1,
    gap: 4,
  },
  upgradeTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '700',
    color: '#FFFFFF',
    lineHeight: 19.6,
  },
  upgradeSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#FFFFFF',
    lineHeight: 12,
  },
  providerCard: {
    padding: 24,
    gap: 20,
    backgroundColor: '#F9F8F3',
    borderWidth: 1,
    borderColor: '#F0EEE4',
    borderRadius: 16,
  },
  providerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  providerLogo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7.5,
  },
  providerText: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#151515',
    lineHeight: 19.6,
  },
  featuresSection: {
    paddingHorizontal: 24,
    gap: 8,
  },
  featureCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    paddingVertical: 12,
  },
  featureContent: {
    flex: 1,
    gap: 4,
  },
  featureTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '700',
    color: '#151515',
    lineHeight: 19.6,
  },
  featureDescription: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '400',
    color: '#757C85',
    lineHeight: 19.6,
  },
  featureTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  plusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingLeft: 4,
    paddingRight: 8,
    backgroundColor: '#669AB7',
    borderRadius: 14,
    height: 24,
  },
  plusBadgeText: {
    fontSize: 10,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#FFFFFF',
    lineHeight: 14,
  },
  divider: {
    height: 1,
    backgroundColor: '#E7EAED',
    marginTop: 24,
  },
  securitySection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  securityText: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '400',
    color: '#757C85',
    lineHeight: 19.6,
  },
  clinicLogo: {
    width: 140,
    resizeMode: 'contain',
    alignSelf: 'flex-start',
  },
  providerNameText: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    maxWidth: 147,
    flex: 1,
  },
  termsDivider: {
    height: 1,
    backgroundColor: '#E7EAED',
  },
  termsLink: {
    paddingHorizontal: 24,
    paddingVertical: 20,
    alignItems: 'flex-start',
  },
  termsLinkText: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#015480',
    textDecorationLine: 'underline',
  },
});
